import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BillableItem,
  BillableItemCreatedEnvelope,
  BillableItemDeletedEnvelope,
  BillableItemQuery,
  BillableItemSearch,
  BillableItemUpdatedEnvelope,
  BillableItemsQueryBuilder,
  BulkCreateBillableItemsOptions,
  BulkCreateBillableItemsResponse,
  BulkDeleteBillableItemsResponse,
  BulkUpdateBillableItemTagsApplicationErrors,
  BulkUpdateBillableItemTagsByFilterApplicationErrors,
  BulkUpdateBillableItemTagsByFilterOptions,
  BulkUpdateBillableItemTagsByFilterResponse,
  BulkUpdateBillableItemTagsOptions,
  BulkUpdateBillableItemTagsResponse,
  BulkUpdateBillableItemsOptions,
  BulkUpdateBillableItemsResponse,
  CreateBillableItemApplicationErrors,
  MaskedBillableItem,
  QueryBillableItemsResponse,
  SearchBillableItemsResponse,
  UpdateBillableItem,
  bulkCreateBillableItems as universalBulkCreateBillableItems,
  bulkDeleteBillableItems as universalBulkDeleteBillableItems,
  bulkUpdateBillableItemTags as universalBulkUpdateBillableItemTags,
  bulkUpdateBillableItemTagsByFilter as universalBulkUpdateBillableItemTagsByFilter,
  bulkUpdateBillableItems as universalBulkUpdateBillableItems,
  createBillableItem as universalCreateBillableItem,
  deleteBillableItem as universalDeleteBillableItem,
  getBillableItem as universalGetBillableItem,
  queryBillableItems as universalQueryBillableItems,
  searchBillableItems as universalSearchBillableItems,
  typedQueryBillableItems as universalTypedQueryBillableItems,
  updateBillableItem as universalUpdateBillableItem,
} from './billable-items-v1-billable-item-billable-items.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/get-paid' };

export function createBillableItem(
  httpClient: HttpClient
): CreateBillableItemSignature {
  return (billableItem: NonNullablePaths<BillableItem, `name` | `price`, 2>) =>
    universalCreateBillableItem(
      billableItem,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateBillableItemSignature {
  /**
   * Creates a billable item.
   * @param - Billable item to create.
   * @returns Created billable item.
   */
  (billableItem: NonNullablePaths<BillableItem, `name` | `price`, 2>): Promise<
    NonNullablePaths<
      BillableItem,
      `name` | `price` | `tags.privateTags.tagIds`,
      4
    > & {
      __applicationErrorsType?: CreateBillableItemApplicationErrors;
    }
  >;
}

export function getBillableItem(
  httpClient: HttpClient
): GetBillableItemSignature {
  return (billableItemId: string) =>
    universalGetBillableItem(
      billableItemId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetBillableItemSignature {
  /**
   * Retrieves a billable item.
   * @param - Billable item ID.
   * @returns Requested billable item.
   */
  (billableItemId: string): Promise<
    NonNullablePaths<
      BillableItem,
      `name` | `price` | `tags.privateTags.tagIds`,
      4
    >
  >;
}

export function updateBillableItem(
  httpClient: HttpClient
): UpdateBillableItemSignature {
  return (
    _id: string,
    billableItem: NonNullablePaths<
      UpdateBillableItem,
      `name` | `price` | `revision`,
      2
    >
  ) =>
    universalUpdateBillableItem(
      _id,
      billableItem,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateBillableItemSignature {
  /**
   * Updates a billable item.
   *
   *
   * Each time the BillableItem is updated,
   * `revision` increments by 1.
   * The current `revision` must be passed when updating the billable item.
   * This ensures you're working with the latest billable item
   * and prevents unintended overwrites.
   * @param - Billable item ID.
   * @returns Updated billable item.
   */
  (
    _id: string,
    billableItem: NonNullablePaths<
      UpdateBillableItem,
      `name` | `price` | `revision`,
      2
    >
  ): Promise<
    NonNullablePaths<
      BillableItem,
      `name` | `price` | `tags.privateTags.tagIds`,
      4
    >
  >;
}

export function deleteBillableItem(
  httpClient: HttpClient
): DeleteBillableItemSignature {
  return (billableItemId: string) =>
    universalDeleteBillableItem(
      billableItemId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteBillableItemSignature {
  /**
   * Permanently deletes a billable item.
   * @param - Billable item ID to delete.
   */
  (billableItemId: string): Promise<void>;
}

export function queryBillableItems(
  httpClient: HttpClient
): QueryBillableItemsSignature {
  return () =>
    universalQueryBillableItems(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryBillableItemsSignature {
  /**
   * Retrieves a list of BillableItems, given the provided [paging, filtering, and sorting](https://dev.wix.com/api/rest/getting-started/sorting-and-paging).
   *
   * Up to 1,000 BillableItems can be returned per request.
   *
   * To learn how to query BillableItems, see [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language).
   */
  (): BillableItemsQueryBuilder;
}

export function typedQueryBillableItems(
  httpClient: HttpClient
): TypedQueryBillableItemsSignature {
  return (query: BillableItemQuery) =>
    universalTypedQueryBillableItems(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryBillableItemsSignature {
  /** */
  (query: BillableItemQuery): Promise<
    NonNullablePaths<
      QueryBillableItemsResponse,
      | `billableItems`
      | `billableItems.${number}.name`
      | `billableItems.${number}.price`,
      4
    >
  >;
}

export function bulkCreateBillableItems(
  httpClient: HttpClient
): BulkCreateBillableItemsSignature {
  return (
    billableItems: BillableItem[],
    options?: BulkCreateBillableItemsOptions
  ) =>
    universalBulkCreateBillableItems(
      billableItems,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateBillableItemsSignature {
  /**
   * Create multiple billable items.
   * @param - List of billable items to create.
   * @param - Field options.
   */
  (
    billableItems: BillableItem[],
    options?: BulkCreateBillableItemsOptions
  ): Promise<
    NonNullablePaths<
      BulkCreateBillableItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.name`
      | `results.${number}.item.price`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateBillableItems(
  httpClient: HttpClient
): BulkUpdateBillableItemsSignature {
  return (
    billableItems: NonNullablePaths<
      MaskedBillableItem,
      `billableItem` | `billableItem._id` | `billableItem.revision`,
      3
    >[],
    options?: BulkUpdateBillableItemsOptions
  ) =>
    universalBulkUpdateBillableItems(
      billableItems,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateBillableItemsSignature {
  /**
   * Updates multiple billable items.
   * @param - Billable items to update.
   * @param - Field options.
   */
  (
    billableItems: NonNullablePaths<
      MaskedBillableItem,
      `billableItem` | `billableItem._id` | `billableItem.revision`,
      3
    >[],
    options?: BulkUpdateBillableItemsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateBillableItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.name`
      | `results.${number}.item.price`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkDeleteBillableItems(
  httpClient: HttpClient
): BulkDeleteBillableItemsSignature {
  return (billableItemIds: string[]) =>
    universalBulkDeleteBillableItems(
      billableItemIds,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteBillableItemsSignature {
  /**
   * Delete multiple billable items.
   * @param - Billable item IDs to delete.
   */
  (billableItemIds: string[]): Promise<
    NonNullablePaths<
      BulkDeleteBillableItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function bulkUpdateBillableItemTags(
  httpClient: HttpClient
): BulkUpdateBillableItemTagsSignature {
  return (
    billableItemIds: string[],
    options?: BulkUpdateBillableItemTagsOptions
  ) =>
    universalBulkUpdateBillableItemTags(
      billableItemIds,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateBillableItemTagsSignature {
  /**
   * Updates tags for multiple billable items.
   * A tag that appears both in the list of assign and unassign tags, will be assigned.
   * @param - IDs of billable items which tags will be updated.
   * @param - Field options.
   */
  (
    billableItemIds: string[],
    options?: BulkUpdateBillableItemTagsOptions
  ): Promise<
    NonNullablePaths<
      BulkUpdateBillableItemTagsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    > & {
      __applicationErrorsType?: BulkUpdateBillableItemTagsApplicationErrors;
    }
  >;
}

export function bulkUpdateBillableItemTagsByFilter(
  httpClient: HttpClient
): BulkUpdateBillableItemTagsByFilterSignature {
  return (
    filter: Record<string, any>,
    options?: BulkUpdateBillableItemTagsByFilterOptions
  ) =>
    universalBulkUpdateBillableItemTagsByFilter(
      filter,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateBillableItemTagsByFilterSignature {
  /**
   * Updates tags on multiple billable items by filter. Works asynchronously, and returns a job ID.
   *
   * An empty filter will update all billable items.
   *
   * A tag that is specified both in the list of assign and unassign tags, will be assigned.
   *
   * Specify the returned jobId in Get Async Job ([SDK](https://dev.wix.com/docs/sdk/backend-modules/async-jobs/get-async-job) | [REST](https://dev.wix.com/docs/rest/business-management/async-job/get-async-job)) to track the job's status.
   * @param - Filter to select relevant billable items.
   * @param - Field options.
   */
  (
    filter: Record<string, any>,
    options?: BulkUpdateBillableItemTagsByFilterOptions
  ): Promise<
    NonNullablePaths<BulkUpdateBillableItemTagsByFilterResponse, `jobId`, 2> & {
      __applicationErrorsType?: BulkUpdateBillableItemTagsByFilterApplicationErrors;
    }
  >;
}

export function searchBillableItems(
  httpClient: HttpClient
): SearchBillableItemsSignature {
  return (search: BillableItemSearch) =>
    universalSearchBillableItems(
      search,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchBillableItemsSignature {
  /**
   * Retrieves a list of up to 100 billable items, given the specified filtering, search expression, sorting, and cursor paging.
   *
   * Learn more about [API query language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language).
   * @param - Search options.
   */
  (search: BillableItemSearch): Promise<
    NonNullablePaths<
      SearchBillableItemsResponse,
      | `billableItems`
      | `billableItems.${number}.name`
      | `billableItems.${number}.price`
      | `aggregationData.results`
      | `aggregationData.results.${number}.scalar.type`
      | `aggregationData.results.${number}.scalar.value`
      | `aggregationData.results.${number}.name`
      | `aggregationData.results.${number}.type`
      | `aggregationData.results.${number}.fieldPath`,
      6
    >
  >;
}

export const onBillableItemCreated = EventDefinition(
  'wix.billable_items.v1.billable_item_created',
  true,
  (event: BillableItemCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.image' }],
        },
      ])
    )
)<BillableItemCreatedEnvelope>();
export const onBillableItemDeleted = EventDefinition(
  'wix.billable_items.v1.billable_item_deleted',
  true,
  (event: BillableItemDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.image' }],
        },
      ])
    )
)<BillableItemDeletedEnvelope>();
export const onBillableItemUpdated = EventDefinition(
  'wix.billable_items.v1.billable_item_updated',
  true,
  (event: BillableItemUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.createdDate' },
            { path: 'modifiedFields.updatedDate' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.image' }, { path: 'modifiedFields.image' }],
        },
      ])
    )
)<BillableItemUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Aggregation,
  AggregationData,
  AggregationKindOneOf,
  AggregationResults,
  AggregationResultsResultOneOf,
  AggregationResultsScalarResult,
  AggregationType,
  ApplicationError,
  BaseEventMetadata,
  BillableItem,
  BillableItemCreatedEnvelope,
  BillableItemDeletedEnvelope,
  BillableItemQuerySpec,
  BillableItemSearchSpec,
  BillableItemUpdatedEnvelope,
  BillableItemsQueryBuilder,
  BillableItemsQueryResult,
  BulkActionMetadata,
  BulkBillableItemResult,
  BulkCreateBillableItemsOptions,
  BulkCreateBillableItemsRequest,
  BulkCreateBillableItemsResponse,
  BulkDeleteBillableItemsRequest,
  BulkDeleteBillableItemsResponse,
  BulkDeleteBillableItemsResponseBulkBillableItemResult,
  BulkUpdateBillableItemTagsByFilterOptions,
  BulkUpdateBillableItemTagsByFilterRequest,
  BulkUpdateBillableItemTagsByFilterResponse,
  BulkUpdateBillableItemTagsOptions,
  BulkUpdateBillableItemTagsRequest,
  BulkUpdateBillableItemTagsResponse,
  BulkUpdateBillableItemTagsResult,
  BulkUpdateBillableItemsOptions,
  BulkUpdateBillableItemsRequest,
  BulkUpdateBillableItemsResponse,
  BulkUpdateBillableItemsResponseBulkBillableItemResult,
  CreateBillableItemRequest,
  CreateBillableItemResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  CursorSearch,
  CursorSearchPagingMethodOneOf,
  Cursors,
  DateHistogramAggregation,
  DateHistogramResult,
  DateHistogramResults,
  DeleteBillableItemRequest,
  DeleteBillableItemResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  GetBillableItemRequest,
  GetBillableItemResponse,
  GroupByValueResults,
  IdentificationData,
  IdentificationDataIdOneOf,
  IncludeMissingValuesOptions,
  InternalCursorPaging,
  InternalCursorQuery,
  InternalCursorQueryPagingMethodOneOf,
  InternalQueryBillableItemsRequest,
  InternalQueryBillableItemsResponse,
  Interval,
  ItemMetadata,
  MaskedBillableItem,
  MessageEnvelope,
  MissingValues,
  Mode,
  NestedAggregation,
  NestedAggregationItem,
  NestedAggregationItemKindOneOf,
  NestedAggregationResults,
  NestedAggregationResultsResultOneOf,
  NestedAggregationType,
  NestedResultValue,
  NestedResultValueResultOneOf,
  NestedResults,
  NestedValueAggregationResult,
  PagingMetadataV2,
  QueryBillableItemsRequest,
  QueryBillableItemsResponse,
  RangeAggregation,
  RangeAggregationResult,
  RangeBucket,
  RangeResult,
  RangeResults,
  RestoreInfo,
  Results,
  ScalarAggregation,
  ScalarResult,
  ScalarType,
  SearchBillableItemsRequest,
  SearchBillableItemsResponse,
  SearchDetails,
  SortDirection,
  SortOrder,
  SortType,
  Sorting,
  TagList,
  Tags,
  UpdateBillableItem,
  UpdateBillableItemRequest,
  UpdateBillableItemResponse,
  ValueAggregation,
  ValueAggregationOptionsOneOf,
  ValueAggregationResult,
  ValueResult,
  ValueResults,
  WebhookIdentityType,
  utils,
} from './billable-items-v1-billable-item-billable-items.universal.js';
